<script>
  // Prefix for the ids that tie each field to its visible label.
  const fid = `f${Math.random().toString(36).slice(2, 8)}`;
  import { link } from 'svelte-spa-router';
  import { t } from '../i18n/index.js';
  import { paymentMethodsApi } from '../stores/api.js';
  import { formatDate } from '../lib/labels.js';
  import {
    dueDateFromDays, todayCalendarDay, PAYMENT_DEFAULT_DAYS,
    instalmentPlanOf, shouldApplyDefaultPreset,
  } from '../lib/payment.js';
  import InfoHint from './InfoHint.svelte';

  /**
   * DatiPagamento, for every screen that collects it.
   *
   * It was the creation form's own markup until the draft editor needed the
   * same four fields. A draft's payment terms are what promotion hands to the
   * XML generator *and* to the scadenzario seeding, so two editors would have
   * had to agree forever about a block that decides when the customer is told
   * to pay — the same reason `LineItemsEditor` is shared rather than copied.
   * The object they both send is built by `lib/payment.js`; this file only
   * collects it.
   *
   * `anchorDate` is the one real difference between the two callers, and it is
   * a date rather than a mode flag on purpose: an invoice being created knows
   * the day it carries, a draft does not have one yet. Everything the operator
   * is told about the due date follows from that.
   */
  export let terms;
  /**
   * The saved preset in use, if any.
   *
   * It lives in the parent, not here, for two reasons: the parent needs it for
   * the DettaglioPagamento fields the four inputs have no room for, and this
   * component is unmounted every time the creation wizard leaves its payment
   * step. Holding the selection locally would mean coming back to that step
   * with an empty picker.
   *
   * It does **not**, on its own, stop the default preset being applied a second
   * time on remount: «Inserimento manuale» leaves `method` null, exactly as an
   * untouched form does, so an operator who typed their own terms and stepped
   * forward to the summary came back to the company preset written over them.
   * `presetChosen` is what separates the two. See `shouldApplyDefaultPreset()`.
   */
  export let method = null;
  /**
   * Whether a preset decision has already been taken on this document — the
   * operator using the picker, «Inserimento manuale» included, or the company
   * default having been applied once already. Parent-held for the same reason
   * `method` is: this component disappears on every wizard step change, and
   * the answer must not.
   */
  export let presetChosen = false;
  export let companyId = '';
  /** The invoice date, `YYYY-MM-DD`. Null on a draft, which has none until it is issued. */
  export let anchorDate = null;
  /**
   * Apply the company's default payment method as soon as the presets load.
   * False when the document already states terms of its own — applying a
   * preset over them would overwrite what the operator, or the system that
   * created the draft, deliberately chose.
   */
  export let autoApplyDefault = true;

  let paymentMethods = [];
  // Which company the list on screen belongs to, so switching company reloads
  // it exactly once instead of on every reactive pass.
  let loadedForCompanyId = '';

  // Read off the preset the parent holds rather than tracked here, so the
  // picker still shows the chosen method after a remount.
  $: selectedPaymentMethodId = method?.id || '';

  // The instalments the stored block carries, if it carries any. They are more
  // than these four inputs can say, so they are shown and not offered for
  // editing — see lib/payment.js, which hands such a block back untouched.
  $: instalments = instalmentPlanOf(terms);

  $: if (companyId && companyId !== loadedForCompanyId) loadPaymentMethods(companyId);

  async function loadPaymentMethods(id) {
    loadedForCompanyId = id;
    try {
      paymentMethods = await paymentMethodsApi.list(id);
      // Never over a decision already taken — see shouldApplyDefaultPreset().
      // This runs again on every remount, and the wizard remounts this
      // component every time the operator leaves the payment step.
      if (shouldApplyDefaultPreset({ autoApplyDefault, method, presetChosen, terms })) {
        const preset = paymentMethods.find((m) => m.isDefault);
        if (preset) applyPaymentMethod(preset.id);
      }
    } catch {
      // A company with no presets, or a request that failed: the four fields
      // below are the whole feature, the picker is a shortcut over them.
      paymentMethods = [];
    }
  }

  function applyPaymentMethod(id) {
    const preset = id ? paymentMethods.find((p) => p.id === id) : null;
    method = preset || null;
    // Recorded even for «Inserimento manuale», which is the case this exists
    // for: choosing to type the terms by hand is a choice, and a remount must
    // not answer it with the company default.
    presetChosen = true;
    if (!preset) return;
    terms = {
      ...terms,
      modalitaPagamento: preset.modalitaPagamento || terms.modalitaPagamento,
      condizioni: preset.condizioni || terms.condizioni,
      iban: preset.iban || terms.iban,
      giorni: preset.scadenzaGiorni != null ? preset.scadenzaGiorni : terms.giorni,
    };
  }

  /** Drop an inherited absolute due date and go back to terms in days. */
  function clearFixedDate() {
    terms = { ...terms, dataScadenza: '' };
  }

  /**
   * Choose how a draft states its due date. Leaving «Scadenza fissa» drops the
   * date, so a day the operator stopped wanting is not sent as a leftover.
   */
  function chooseDueMode(fixed) {
    terms = fixed
      ? { ...terms, fixedMode: true }
      : { ...terms, fixedMode: false, dataScadenza: '' };
  }

  // The date the document will actually ask to be paid on. On a draft there is
  // no anchor, so this is null and the operator is shown what today would give
  // instead — clearly labelled as a preview, never as the stored answer.
  $: dueDate = dueDateFromDays(anchorDate, terms.giorni === '' ? PAYMENT_DEFAULT_DAYS : terms.giorni);
  $: previewDueDate = anchorDate
    ? null
    : dueDateFromDays(todayCalendarDay(),
      terms.giorni === '' ? PAYMENT_DEFAULT_DAYS : terms.giorni);

  const inputCls = 'input';
  const inputStyle = '';
  const labelCls = 'block eyebrow mb-1';
  const labelStyle = 'color: var(--color-text-muted);';
</script>

<!-- A plan in instalments: several amounts, each on its own day, which the four
     fields below cannot describe. Shown in full and left exactly as stored, the
     same way an inherited fixed due date is shown rather than silently kept —
     an editor that cannot express a plan must not replace it with one it can. -->
{#if instalments}
  <div class="card p-4 border-yellow-500">
    <p class="eyebrow mb-2" style="color: var(--color-primary);">
      {$t('paymentTerms.planTitle', { rate: instalments.length })}
    </p>
    <ul class="space-y-1">
      {#each instalments as rata, i}
        <li class="text-xs font-mono" style="color: var(--color-text-body);">
          {$t('paymentTerms.planRow', {
            numero: i + 1,
            importo: rata.importoPagamento || '—',
            scadenza: rata.dataScadenza
              ? formatDate(rata.dataScadenza)
              : $t('paymentTerms.planRowNoDate'),
          })}
        </li>
      {/each}
    </ul>
    <p class="mt-3 text-xs measure" style="color: var(--color-text-muted);">
      {$t('paymentTerms.planNotice')}
    </p>
  </div>
{:else}
  {#if paymentMethods.length > 0}
    <div class="mb-4 flex flex-col md:flex-row md:items-end gap-3">
      <label class="flex flex-col flex-1">
        <span id="{fid}-1" class={labelCls} style={labelStyle}>{$t('paymentMethods.picker.label')}</span>
        <select aria-labelledby="{fid}-1" class={inputCls} style={inputStyle} value={selectedPaymentMethodId}
          on:change={(e) => applyPaymentMethod(e.currentTarget.value)}>
          <option value="">{$t('paymentMethods.picker.none')}</option>
          {#each paymentMethods as m}
            <option value={m.id}>{m.label}{m.isDefault ? ' (★)' : ''}</option>
          {/each}
        </select>
      </label>
      <!-- use:link keeps this a hash navigation: a plain href would trigger a
           full document reload and throw away everything already typed. -->
      <a href="/settings/payment-methods" use:link class="text-xs underline" style="color: var(--color-text-muted);">{$t('paymentMethods.picker.manageLink')}</a>
    </div>
  {:else}
    <p class="text-xs italic mb-3" style="color: var(--color-text-muted);">
      <a href="/settings/payment-methods" use:link class="underline">{$t('paymentMethods.picker.manageLink')}</a> — {$t('paymentMethods.empty')}
    </p>
  {/if}

  <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
    <div>
      <span id="{fid}-2" class={labelCls} style={labelStyle}>{$t('invoiceCreate.fields.conditions')}</span>
      <select aria-labelledby="{fid}-2" bind:value={terms.condizioni} class={inputCls} style={inputStyle}>
        <option value="TP01">{$t('invoiceCreate.paymentConditions.TP01')}</option>
        <option value="TP02">{$t('invoiceCreate.paymentConditions.TP02')}</option>
        <option value="TP03">{$t('invoiceCreate.paymentConditions.TP03')}</option>
      </select>
    </div>
    <div>
      <span id="{fid}-3" class={labelCls} style={labelStyle}>{$t('invoiceCreate.fields.method')}</span>
      <select aria-labelledby="{fid}-3" bind:value={terms.modalitaPagamento} class={inputCls} style={inputStyle}>
        <option value="MP05">{$t('invoiceCreate.paymentMethods.MP05')}</option>
        <option value="MP01">{$t('invoiceCreate.paymentMethods.MP01')}</option>
        <option value="MP08">{$t('invoiceCreate.paymentMethods.MP08')}</option>
        <option value="MP12">{$t('invoiceCreate.paymentMethods.MP12')}</option>
        <option value="">{$t('invoiceCreate.paymentMethods.none')}</option>
      </select>
    </div>
    <div>
      <span id="{fid}-4" class={labelCls} style={labelStyle}>{$t('invoiceCreate.fields.iban')}</span>
      <input aria-labelledby="{fid}-4" bind:value={terms.iban} maxlength="34" class={inputCls} style={inputStyle} />
    </div>
    <div>
      <span id="{fid}-5" class="{labelCls} inline-flex items-center gap-1" style={labelStyle}>
        {$t('invoiceCreate.fields.dueDays')}<InfoHint text={$t('paymentMethods.fieldHints.scadenzaGiorni')} />
      </span>
      <input aria-labelledby="{fid}-5" bind:value={terms.giorni} type="number" min="0" class={inputCls} style={inputStyle}
        disabled={!anchorDate && terms.fixedMode} />
    </div>
  </div>

  <!-- A draft has no date to count the days from, so by default its due date
       moves with the day it is issued. A contract can fix the date instead; the
       choice is offered only where it makes sense — on a draft, once there is a
       payment method to pay by. -->
  {#if !anchorDate && terms.modalitaPagamento}
    <fieldset class="mt-4">
      <legend class="{labelCls} inline-flex items-center gap-1" style={labelStyle}>
        {$t('paymentTerms.dueModeLabel')}<InfoHint term="scadenzaFissa" />
      </legend>
      <div class="flex flex-col md:flex-row md:gap-6 gap-2">
        <label class="inline-flex items-center gap-2 text-sm" style="color: var(--color-text-body);">
          <input type="radio" name="payment-due-mode" checked={!terms.fixedMode} on:change={() => chooseDueMode(false)} />
          {$t('paymentTerms.modeDays')}
        </label>
        <label class="inline-flex items-center gap-2 text-sm" style="color: var(--color-text-body);">
          <input type="radio" name="payment-due-mode" checked={terms.fixedMode} on:change={() => chooseDueMode(true)} />
          {$t('paymentTerms.modeFixed')}
        </label>
      </div>
      {#if terms.fixedMode}
        <div class="mt-3 max-w-xs">
          <label class={labelCls} style={labelStyle} for="payment-fixed-date">{$t('paymentTerms.fixedDateLabel')}</label>
          <input id="payment-fixed-date" type="date" bind:value={terms.dataScadenza} class={inputCls} style={inputStyle} />
        </div>
        <p class="mt-2 text-xs measure" style="color: var(--color-text-muted);">{$t('paymentTerms.fixedModeNotice')}</p>
      {/if}
    </fieldset>
  {/if}

  <!-- What these four fields actually produce, said in words. A silent default
       here is a due date nobody agreed to, and the operator reading this screen
       may never have issued an electronic invoice before. -->
  {#if !terms.modalitaPagamento}
    <p class="mt-3 text-xs measure" style="color: var(--color-text-muted);">{$t('paymentTerms.noneNotice')}</p>
  {:else}
    {#if terms.dataScadenza && !anchorDate && !terms.fixedMode}
      <div class="mt-3 card p-3 border-yellow-500">
        <p class="text-xs measure" style="color: var(--color-text-body);">
          {$t('paymentTerms.fixedDateNotice', { scadenza: formatDate(terms.dataScadenza) })}
        </p>
        <button type="button" class="btn btn-secondary btn-sm mt-2" on:click={clearFixedDate}>
          {$t('paymentTerms.fixedDateClear')}
        </button>
      </div>
    {:else if anchorDate && dueDate}
      <p class="mt-3 text-xs measure" style="color: var(--color-text-muted);">
        {$t('paymentTerms.dueOn', {
          scadenza: formatDate(dueDate),
          giorni: terms.giorni === '' ? PAYMENT_DEFAULT_DAYS : terms.giorni,
          data: formatDate(anchorDate),
        })}
      </p>
    {/if}

    {#if !anchorDate}
      {#if !terms.fixedMode}
        <p class="mt-3 text-xs measure" style="color: var(--color-text-muted);">{$t('paymentTerms.draftNotice')}</p>
      {/if}
      {#if previewDueDate && !terms.dataScadenza && !terms.fixedMode}
        <p class="mt-1 text-xs measure" style="color: var(--color-text-muted);">
          {$t('paymentTerms.draftPreview', { scadenza: formatDate(previewDueDate) })}
        </p>
      {/if}
    {/if}
  {/if}
{/if}
